import { apiClient } from './apiClient';
import type { PostOpsSortKey, PostOpsRowStatus } from '@/pages/post-ops/postOpsShared';

/**
 * The Post-ops worklist.
 *
 * Mirrors GET /v5/post-ops (api/app/Http/Controllers/V5/PostOpsController.php).
 * The endpoint is gated server-side by the `post ops` module AND legacy
 * permission 7, so a 403 here is expected for accounts without both.
 */

export interface PostOpsListItem {
  /** null when the plan has no post-op record yet. */
  postopRequestId: number | null;
  planId: number;
  patientId: number;
  patientDisplayId: string | null;
  patientName: string;
  doctorName: string;
  procedures: string;
  procedureDate: string | null;
  lastVisit: string | null;
  /** null when there is no appointment later than today. */
  nextVisit: string | null;
  /** '' when the plan has no post-op record. */
  status: string;
  statusId: number | null;
  rowStatus: PostOpsRowStatus;
  isQa: boolean;
  qaResolved: boolean;
  reasons: string;
  surveyResult: string;
  photoConsent: string;
  updatedAt: string | null;
}

/** The six stat tiles, keyed as the server returns them. */
export interface PostOpsTiles {
  procedures: number;
  inFollowUp: number;
  needsFollowUp: number;
  inQa: number;
  dischargedHappy: number;
  lostToFollowUp: number;
}

export interface PostOpsListMeta {
  total: number;
  page: number;
  perPage: number;
  totalPages: number;
  /** null when the requested sort key was not recognised. */
  sort: PostOpsSortKey | null;
  sortDir: 'asc' | 'desc';
  tab: number;
  tiles: PostOpsTiles;
}

export interface PostOpsListParams {
  tab?: number;
  page?: number;
  perPage?: number;
  sortBy?: PostOpsSortKey;
  sortDir?: 'asc' | 'desc';
  search?: string;
  searchType?: 'name' | 'planId' | 'patientId';
  statusIds?: number[];
  qaStatusIds?: string[];
  doctorIds?: number[];
  facilityIds?: number[];
  reasonIds?: number[];
  procDateFrom?: string;
  procDateTo?: string;
  /**
   * The negated sidebar filter keys, as a list -- the wire shape every V5
   * list page uses. Never a {key: boolean} map: legacy stores that shape
   * with the opposite meaning.
   */
  filterPlus?: string[];
}

export interface PostOpsListResponse {
  data: PostOpsListItem[];
  meta: PostOpsListMeta;
}

/**
 * Array params go over the wire as CSV, which is what the server's filter
 * normaliser accepts alongside repeated keys.
 */
function toQuery(params: PostOpsListParams): Record<string, unknown> {
  const query: Record<string, unknown> = {};

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;

    if (Array.isArray(value)) {
      if (value.length === 0) return;
      query[key] = value.join(',');
      return;
    }

    query[key] = value;
  });

  return query;
}

export function listPostOps(params: PostOpsListParams = {}): Promise<PostOpsListResponse> {
  return apiClient
    .get<PostOpsListResponse>('/post-ops', { params: toQuery(params) })
    .then((r) => r.data);
}

/** One entry in the account's active Q/A reason catalogue. */
export interface PostOpsReasonType {
  id: number;
  title: string;
}

/**
 * The account's active Q/A reasons, ordered for display.
 *
 * Backed by `postop_reasons_type`, which is a separate catalogue from the
 * `qa_reasons` list behind V5's own QA page.
 */
export function listPostOpsReasonTypes(): Promise<PostOpsReasonType[]> {
  return apiClient
    .get<{ data: PostOpsReasonType[] }>('/post-ops/reason-types')
    .then((r) => r.data.data ?? []);
}
